import { useAtom } from "jotai";
import React, { useContext } from "react";
import { toast } from "react-toastify";
import tools from "../../../helpers/tools";
import { withRetry } from "../../../helpers/withRetry";
import useManagers from "../../../hooks/useManagers/useManagers";
import { searchLeads } from "../../../services/leadService";
import {
  exportProgressAtom,
  isExportingAllAtom,
  isListInitAtom,
  isSearchingAtom,
  leadCountAtom,
  leadsAtom,
  leadsFiltersAtom,
} from "./atoms/leadListAtoms";

const EXPORT_BATCH_SIZE = 3;

export const LeadPageContext = React.createContext();
export const LeadProvider = ({ value, children }) => {
  return (
    <LeadPageContext.Provider value={value}>
      {children}
    </LeadPageContext.Provider>
  );
};
export const filtersByProduct = {
  PRO: {
    page: 1,
    filters: {
      terms: "",
      products: [],
      assignedTo: [],
      statuses: [],
      beginAt: "",
      endAt: "",
      producer: "",
      onlyFinalized: false,
      prochaineTache: "",
    },
  },
  DOMI: {
    page: 1,
    filters: {
      terms: "",
      products: [],
      assignedTo: [],
      statuses: [],
      beginAt: "",
      endAt: "",
      prochaineTache: "",
    },
  },
};

const useLeads = () => {
  const { productCategory } = useContext(LeadPageContext);
  const [leads, setLeads] = useAtom(leadsAtom);
  const [leadsFilters, setLeadsFilters] = useAtom(leadsFiltersAtom);
  const [isSearching, setIsSearching] = useAtom(isSearchingAtom);
  const [leadCount, setLeadCount] = useAtom(leadCountAtom);
  const [isListInit, setIsListInit] = useAtom(isListInitAtom);
  const [isExportingAll, setIsExportingAll] = useAtom(isExportingAllAtom);
  const [exportProgress, setExportProgress] = useAtom(exportProgressAtom);
  const { managers, contacts } = useManagers();

  const resetAtoms = ({ newLeadFilters }) => {
    let freshFilters = filtersByProduct[productCategory];
    if (newLeadFilters) {
      freshFilters = {
        ...freshFilters,
        filters: { ...freshFilters.filters, ...newLeadFilters },
      };
    }
    setLeads([]);
    setIsListInit(false);
    setIsSearching(false);
    setLeadCount(0);
    setLeadsFilters(freshFilters);
    onGetLeads(freshFilters);
  };

  const getFormattedLeads = (apiLeads) => {
    const newLeads = apiLeads.map((d) => {
      return {
        ...d,
        tasks: tools.populateNamesFromId(managers, d.tasks),
        comments: tools.populateNamesFromId(contacts, d.comments),
      };
    });
    return newLeads;
  };

  const onGetLeads = (overrideFilters = null) => {
    let formattedFilters = overrideFilters || {
      ...leadsFilters,
      filters: { ...leadsFilters.filters, leadId: undefined },
    };
    formattedFilters = {
      ...formattedFilters,
      filters: {
        ...formattedFilters.filters,
        beginAt: formattedFilters.filters.beginAt || "",
        endAt: formattedFilters.filters.endAt || "",
      },
    };
    setIsSearching(true);
    searchLeads(formattedFilters, productCategory)
      .then((data) => {
        setLeadCount(data.count);
        setLeads(getFormattedLeads(data.data));
        setIsListInit(true);
      })
      .finally(() => setIsSearching(false));
  };

  const handleExportAllJSON = async () => {
    if (isExportingAll) return;

    setIsExportingAll(true);
    try {
      const baseFilters = {
        ...leadsFilters,
        filters: {
          ...leadsFilters.filters,
          beginAt: leadsFilters.filters.beginAt || "",
          endAt: leadsFilters.filters.endAt || "",
          leadId: undefined,
        },
      };

      const firstResponse = await withRetry(() =>
        searchLeads(
          { ...baseFilters, page: 1 },
          productCategory
        ),
      );

      const total = firstResponse.count;
      if (!firstResponse.data?.length) {
        toast.error("Page 1 vide, export annulé");
        return;
      }

      // On NE calcule PAS le nombre de pages à partir d'une taille mesurée :
      // un éventuel doublon dans une page fausserait la mesure (cf. bug "21 au
      // lieu de 20"). On boucle tant qu'il reste des leads uniques à récupérer
      // (ou jusqu'à une page vide), et on déduplique par id au fil de l'eau —
      // ce qui neutralise les doublons et fiabilise la condition d'arrêt.
      const leadsById = new Map();
      const addLeads = (data = []) => {
        data.forEach((lead) => {
          if (lead?.id != null) leadsById.set(lead.id, lead);
        });
      };
      addLeads(firstResponse.data);

      // Estimation indicative de progression (peut bouger si doublons),
      // bornée pour ne jamais afficher 0.
      const estimatedTotalPages = Math.max(
        1,
        Math.ceil(total / firstResponse.data.length),
      );
      setExportProgress({ current: 1, total: estimatedTotalPages });

      let nextPage = 2;
      let reachedEnd = false;
      // Garde-fou : borne très large pour éviter toute boucle infinie en cas
      // d'anomalie serveur (le serveur paginant ~20/page, ceci couvre largement).
      const maxPages = estimatedTotalPages + 50;

      while (!reachedEnd && leadsById.size < total && nextPage <= maxPages) {
        const batchPages = [];
        for (
          let p = nextPage;
          p < nextPage + EXPORT_BATCH_SIZE && p <= maxPages;
          p++
        ) {
          batchPages.push(p);
        }

        const batchResults = await Promise.all(
          batchPages.map((page) =>
            withRetry(() =>
              searchLeads({ ...baseFilters, page }, productCategory),
            ).then((res) => ({ page, res })),
          ),
        );

        const batchEmpty = batchResults.some(
          ({ res }) => (res.data || []).length === 0,
        );
        batchResults.forEach(({ res }) => addLeads(res.data || []));
        if (batchEmpty) reachedEnd = true;

        nextPage += EXPORT_BATCH_SIZE;
        setExportProgress({
          current: Math.min(nextPage - 1, estimatedTotalPages),
          total: estimatedTotalPages,
        });
      }

      const allLeads = Array.from(leadsById.values());

      console.log(
        `[ExportAll] total leads fetched: ${allLeads.length} / expected ${total} (diff ${
          total - allLeads.length
        })`,
      );

      if (allLeads.length < total) {
        toast.warning(
          `Attention : ${allLeads.length}/${total} leads exportés. Voir la console pour les détails.`,
        );
      }

      const exportData = allLeads.map((lead) => {
        const { tasks, comments, ...rest } = lead;
        return rest;
      });

      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `leads_${productCategory.toLowerCase()}_${
        new Date().toISOString().split("T")[0]
      }.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`${exportData.length} leads exportés`);
    } catch (err) {
      console.log("err", err);
      toast.error("Erreur lors de l'export des leads");
    } finally {
      setIsExportingAll(false);
      setExportProgress({ current: 0, total: 0 });
    }
  };

  return {
    leads,
    leadCount,
    isListInit,
    isSearching,
    productCategory,
    isExportingAll,
    exportProgress,
    actions: {
      getLeads: onGetLeads,
      resetAtoms,
      handleExportAllJSON,
    },
  };
};

export default useLeads;
